// ─── Jobs Library guided tour ─────────────────────────────────────────────────
export const TOUR_MODULE_KEY = "jobs-library";


export const CROSS_PAGE_TOUR_PHASE = {
  JOB_DETAILS: "jobs-library:job-details", // show the toolbar step on Job Details
  FINISH: "jobs-library:finish", // back on Jobs Library, resume at Finish
};

export const TOUR_COLUMN_CLASS = {
  status: "jobs-tour-status-col",
  criticalRole: "jobs-tour-critical-col",
};

export const TOUR_STEPS = [
  {
    // Welcome — centered greeting
    state: "always",
    title: "Welcome to the Jobs Library",
    description:
      "This is where every Job Description in your organization lives. Let's take a quick tour so you know exactly where to start and how to create your first JD.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    state: "always",
    key: "createJd",
    title: "Create Job Description",
    description:
      "This is where every JD starts. Click here to open the Create JD form to submit the appropriate role details (Job Code & Job Family Name, Job Title, Legal Entity, Business, Role Type and more). Our AI generates a complete, ready-to-review Job Description in less than 10 minutes.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "viewToggle",
    title: "View Mode Toggle",
    description:
      "Switch between View by Role ID (a flat list of all individual JDs with their status) and View by Job Code (grouped by role type).",
    placement: "bottom",
  },
  {
    state: "data",
    key: "search",
    title: "Search Bar",
    description:
      "Search for any job by role ID, role name, business, location, function, sub-function, or job level.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "compare",
    title: "Compare JDs Button",
    description:
      "Want to see how two published job descriptions differ from one another? Use the Compare JDs function for a side-by-side view.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "table",
    title: "Jobs Table",
    description:
      "This table lists every job description in your organization to include role ID, role name, business, location, function, sub-function, job level, and version info. Click any row to open the full Job Description.",
    placement: "top",
  },
  {
    // Shown only when the library is empty (replaces the table steps).
    state: "empty",
    key: "emptyState",
    title: "Empty State",
    description:
      "You haven't created any Job Descriptions yet. Once you do, they'll appear here searchable, filterable, and ready for review. Click Create Job Description above to generate your first one.",
    placement: "top",
  },
  {
    state: "data",
    key: "status",
    title: "Status Column & Filter",
    description:
      "Job descriptions are either Published (live and locked for editing) or Unpublished (still being worked on). Click the Status filter to view just one status at a time.",
    placement: "top",
  },
  {
    state: "data",
    key: "criticalRole",
    title: "Critical Role Column",
    description:
      "Check this box to mark a role as business-critical. Critical roles are prioritized in skills planning and AI-impact analysis. You can filter to only see critical roles.",
    placement: "left",
  },
  {
    // On "Next" the tour opens a JD and hands off to the Job Details page, where
    // this step is shown against the toolbar, then returns to the Finish step.
    state: "data",
    key: "jobDetailsToolbar",
    handoffToPage: true,
    title: "Job Details Toolbar",
    description:
      "Once you open a job description this toolbar is your control center:\n\nClick the edit button to make changes to the JD. Revise the JD to have the AI regenerate an updated version to incorporate revisions. Once the JD is published you can download it as a PDF or Word doc (Unpublished JDs can only be downloaded as a PDF). The compare buttons allow you to view different JD versions or see changes. Once the JD is edited, users can submit edits for approval. If no changes are made, you can Publish the JD directly.",
    placement: "bottom",
  },
  {
    // Finish — centered greeting. Primary opens the Create JD drawer.
    state: "always",
    title: "You're all set!",
    description: "Ready to create your first Job Description?",
    primaryText: "Create JD Now",
    secondaryText: "Done",
  },
];

// Active steps for the current branch, preserving spec order.
export const getTourSteps = (isEmpty) =>
  TOUR_STEPS.filter(
    (step) => step.state === "always" || step.state === (isEmpty ? "empty" : "data"),
  );

// The cross-page step, shown by the Job Details page against its toolbar.
export const JOB_DETAILS_TOOLBAR_STEP = TOUR_STEPS.find((step) => step.handoffToPage);
